import type {
  AssignmentRecord,
  DripRecord,
  DrugRecord,
  MedicationRecord,
  ProtocolRecord,
  ResusConfigData,
  WizardState,
  WizardStep,
  Attachment,
  DefiEntry,
  MedicationCase
} from '../types';

export type Action =
  | { type: 'HYDRATE'; data: ResusConfigData }
  | { type: 'SET_STEP'; step: WizardStep }
  | { type: 'SELECT_PROTOCOL'; id: string | null }
  | { type: 'SELECT_MEDICATION'; id: string | null }
  | { type: 'SET_PROTOCOL_SEARCH'; value: string }
  | { type: 'SET_MEDICATION_SEARCH'; value: string }
  | { type: 'SET_PRESET'; preset: 'newborn' | 'child' }
  | { type: 'MARK_CLEAN' }
  | { type: 'ADD_PROTOCOL'; protocol: ProtocolRecord }
  | { type: 'UPDATE_PROTOCOL'; id: string; patch: Partial<ProtocolRecord> }
  | { type: 'DELETE_PROTOCOL'; id: string }
  | { type: 'ADD_PROTOCOL_ATTACHMENT'; protocolId: string; attachment: Attachment }
  | { type: 'UPDATE_PROTOCOL_ATTACHMENT'; protocolId: string; index: number; patch: Partial<Attachment> }
  | { type: 'REMOVE_PROTOCOL_ATTACHMENT'; protocolId: string; index: number }
  | { type: 'SET_DEFI'; protocolId: string; defi: DefiEntry[] }
  | { type: 'ADD_MEDICATION'; medication: MedicationRecord }
  | { type: 'UPDATE_MEDICATION'; id: string; patch: Partial<DrugRecord> & Partial<DripRecord> }
  | { type: 'UPDATE_MEDICATION_CASES'; id: string; cases: MedicationCase[] | null }
  | { type: 'DELETE_MEDICATION'; id: string }
  | { type: 'TOGGLE_MEDICATION_IN_DRUG_SECTION'; medicationId: string; sectionName: string }
  | { type: 'TOGGLE_ASSIGNMENT'; protocolId: string; medicationId: string }
  | { type: 'REORDER_ASSIGNMENTS'; protocolId: string; orderedMedicationIds: string[] }
  | { type: 'SET_ASSIGNMENT_COMMENT'; protocolId: string; medicationId: string; comment: string }
  | { type: 'BULK_SET_PROTOCOL_ASSIGNMENTS'; protocolId: string; medicationIds: string[] };

export function makeInitialState(data: ResusConfigData): WizardState {
  return {
    data,
    ui: {
      selectedProtocolId: data.protocols[0]?.id ?? null,
      selectedMedicationId: data.medications[0]?.id ?? null,
      currentStep: 1,
      dirty: false,
      errors: [],
      sampleWeightPreset: 'child',
      protocolSearch: '',
      medicationSearch: ''
    }
  };
}

function dirty(state: WizardState, data: ResusConfigData): WizardState {
  return { ...state, data, ui: { ...state.ui, dirty: true } };
}

function reorderAssignments(
  assignments: AssignmentRecord[],
  protocolId: string,
  orderedIds: string[]
): AssignmentRecord[] {
  const orderMap = new Map(orderedIds.map((id, idx) => [id, idx]));
  return assignments.map(a => {
    if (a.protocolId !== protocolId) return a;
    const newOrder = orderMap.get(a.medicationId);
    if (newOrder === undefined) return a;
    return { ...a, order: newOrder };
  });
}

export function reducer(state: WizardState, action: Action): WizardState {
  switch (action.type) {
    case 'HYDRATE':
      return makeInitialState(action.data);

    case 'SET_STEP':
      return { ...state, ui: { ...state.ui, currentStep: action.step } };

    case 'SELECT_PROTOCOL':
      return { ...state, ui: { ...state.ui, selectedProtocolId: action.id } };

    case 'SELECT_MEDICATION':
      return { ...state, ui: { ...state.ui, selectedMedicationId: action.id } };

    case 'SET_PROTOCOL_SEARCH':
      return { ...state, ui: { ...state.ui, protocolSearch: action.value } };

    case 'SET_MEDICATION_SEARCH':
      return { ...state, ui: { ...state.ui, medicationSearch: action.value } };

    case 'SET_PRESET':
      return { ...state, ui: { ...state.ui, sampleWeightPreset: action.preset } };

    case 'MARK_CLEAN':
      return { ...state, ui: { ...state.ui, dirty: false } };

    case 'ADD_PROTOCOL': {
      return dirty(state, {
        ...state.data,
        protocols: [...state.data.protocols, action.protocol]
      });
    }

    case 'UPDATE_PROTOCOL': {
      const protocols = state.data.protocols.map(p =>
        p.id === action.id ? { ...p, ...action.patch } : p
      );
      return dirty(state, { ...state.data, protocols });
    }

    case 'DELETE_PROTOCOL': {
      const protocols = state.data.protocols.filter(p => p.id !== action.id);
      const assignments = state.data.assignments.filter(a => a.protocolId !== action.id);
      const { [action.id]: _removed, ...defi } = state.data.defibrillation;
      return dirty(state, {
        ...state.data,
        protocols,
        assignments,
        defibrillation: defi
      });
    }

    case 'ADD_PROTOCOL_ATTACHMENT': {
      const protocols = state.data.protocols.map(p =>
        p.id === action.protocolId
          ? { ...p, attachments: [...p.attachments, action.attachment] }
          : p
      );
      return dirty(state, { ...state.data, protocols });
    }

    case 'UPDATE_PROTOCOL_ATTACHMENT': {
      const protocols = state.data.protocols.map(p => {
        if (p.id !== action.protocolId) return p;
        const attachments = p.attachments.map((a, i) =>
          i === action.index ? { ...a, ...action.patch } : a
        );
        return { ...p, attachments };
      });
      return dirty(state, { ...state.data, protocols });
    }

    case 'REMOVE_PROTOCOL_ATTACHMENT': {
      const protocols = state.data.protocols.map(p => {
        if (p.id !== action.protocolId) return p;
        const attachments = p.attachments.filter((_, i) => i !== action.index);
        return { ...p, attachments };
      });
      return dirty(state, { ...state.data, protocols });
    }

    case 'SET_DEFI': {
      const next = { ...state.data.defibrillation };
      if (action.defi.length === 0) {
        delete next[action.protocolId];
      } else {
        next[action.protocolId] = action.defi;
      }
      return dirty(state, { ...state.data, defibrillation: next });
    }

    case 'ADD_MEDICATION': {
      return dirty(state, {
        ...state.data,
        medications: [...state.data.medications, action.medication]
      });
    }

    case 'UPDATE_MEDICATION': {
      const medications = state.data.medications.map(m =>
        m.id === action.id ? ({ ...m, ...action.patch } as MedicationRecord) : m
      );
      return dirty(state, { ...state.data, medications });
    }

    case 'UPDATE_MEDICATION_CASES': {
      const medications = state.data.medications.map(m =>
        m.id === action.id ? ({ ...m, cases: action.cases } as MedicationRecord) : m
      );
      return dirty(state, { ...state.data, medications });
    }

    case 'DELETE_MEDICATION': {
      const medications = state.data.medications.filter(m => m.id !== action.id);
      const assignments = state.data.assignments.filter(a => a.medicationId !== action.id);
      const drugSections = state.data.drugSections.map(s => ({
        ...s,
        medicationIds: s.medicationIds.filter(id => id !== action.id)
      }));
      return dirty(state, {
        ...state.data,
        medications,
        assignments,
        drugSections
      });
    }

    case 'TOGGLE_MEDICATION_IN_DRUG_SECTION': {
      const drugSections = state.data.drugSections.map(s => {
        if (s.name !== action.sectionName) return s;
        const exists = s.medicationIds.includes(action.medicationId);
        return {
          ...s,
          medicationIds: exists
            ? s.medicationIds.filter(id => id !== action.medicationId)
            : [...s.medicationIds, action.medicationId]
        };
      });
      return dirty(state, { ...state.data, drugSections });
    }

    case 'TOGGLE_ASSIGNMENT': {
      const existing = state.data.assignments.find(
        a => a.protocolId === action.protocolId && a.medicationId === action.medicationId
      );
      let assignments: AssignmentRecord[];
      if (existing) {
        assignments = state.data.assignments.filter(
          a => !(a.protocolId === action.protocolId && a.medicationId === action.medicationId)
        );
        // Re-pack order to keep it dense.
        const inProtocol = assignments.filter(a => a.protocolId === action.protocolId)
          .sort((a, b) => a.order - b.order);
        const orderMap = new Map(inProtocol.map((a, i) => [a.medicationId, i]));
        assignments = assignments.map(a =>
          a.protocolId === action.protocolId
            ? { ...a, order: orderMap.get(a.medicationId) ?? a.order }
            : a
        );
      } else {
        const maxOrder = state.data.assignments
          .filter(a => a.protocolId === action.protocolId)
          .reduce((m, a) => Math.max(m, a.order), -1);
        assignments = [
          ...state.data.assignments,
          {
            protocolId: action.protocolId,
            medicationId: action.medicationId,
            order: maxOrder + 1,
            comment: ''
          }
        ];
      }
      return dirty(state, { ...state.data, assignments });
    }

    case 'BULK_SET_PROTOCOL_ASSIGNMENTS': {
      const others = state.data.assignments.filter(a => a.protocolId !== action.protocolId);
      // Preserve existing comments where the medication is still present.
      const existingByMed = new Map(
        state.data.assignments
          .filter(a => a.protocolId === action.protocolId)
          .map(a => [a.medicationId, a])
      );
      const fresh: AssignmentRecord[] = action.medicationIds.map((medId, idx) => {
        const prev = existingByMed.get(medId);
        return {
          protocolId: action.protocolId,
          medicationId: medId,
          order: idx,
          comment: prev?.comment ?? ''
        };
      });
      return dirty(state, { ...state.data, assignments: [...others, ...fresh] });
    }

    case 'REORDER_ASSIGNMENTS': {
      const assignments = reorderAssignments(state.data.assignments, action.protocolId, action.orderedMedicationIds);
      return dirty(state, { ...state.data, assignments });
    }

    case 'SET_ASSIGNMENT_COMMENT': {
      const assignments = state.data.assignments.map(a =>
        a.protocolId === action.protocolId && a.medicationId === action.medicationId
          ? { ...a, comment: action.comment }
          : a
      );
      return dirty(state, { ...state.data, assignments });
    }

    default:
      return state;
  }
}

// Selectors

export function getProtocolsForMedication(data: ResusConfigData, medicationId: string): ProtocolRecord[] {
  const ids = new Set(
    data.assignments.filter(a => a.medicationId === medicationId).map(a => a.protocolId)
  );
  return data.protocols.filter(p => ids.has(p.id));
}

export function getAssignmentsForProtocol(data: ResusConfigData, protocolId: string): AssignmentRecord[] {
  return data.assignments
    .filter(a => a.protocolId === protocolId)
    .sort((a, b) => a.order - b.order);
}

export function getMedicationById(data: ResusConfigData, id: string): MedicationRecord | undefined {
  return data.medications.find(m => m.id === id);
}

export function getProtocolById(data: ResusConfigData, id: string): ProtocolRecord | undefined {
  return data.protocols.find(p => p.id === id);
}

export function findDuplicateIds(data: ResusConfigData): string[] {
  const seen = new Set<string>();
  const dups = new Set<string>();
  for (const p of data.protocols) {
    if (seen.has(p.id)) dups.add(p.id);
    seen.add(p.id);
  }
  const seenMed = new Set<string>();
  for (const m of data.medications) {
    if (seenMed.has(m.id)) dups.add(m.id);
    seenMed.add(m.id);
  }
  return Array.from(dups);
}

export function uniqueProtocolSections(data: ResusConfigData): string[] {
  const set = new Set<string>();
  for (const p of data.protocols) {
    if (p.section) set.add(p.section);
  }
  return Array.from(set);
}
